import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useLang } from '../context/LangContext';
import { API_URL } from '../config';

const API = API_URL;

const Navbar = ({ onCartOpen }) => {
  const { itemCount } = useCart();
  const { lang, t, toggle } = useLang();
  const location = useLocation();
  const [scrolled, setScrolled]     = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchVal, setSearchVal]   = useState('');
  const [menuOpen, setMenuOpen]     = useState(false);
  const [topCategories, setTopCategories] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    // Top 4 catégories qui ont réellement des produits en stock (plutôt qu'une liste figée)
    axios.get(`${API}/products/categories`)
      .then(r => setTopCategories(r.data.data.filter(c => c.id !== 'all').slice(0, 4)))
      .catch(() => setTopCategories([]));
  }, []);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const openSearch = () => { setSearchOpen(true); setTimeout(() => inputRef.current?.focus(), 50); };
  const closeSearch = () => { setSearchOpen(false); setSearchVal(''); };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchVal.trim()) return;
    const el = document.getElementById('products');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    const input = document.querySelector('.search-bar input');
    if (input) {
      const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      setter.call(input, searchVal);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    closeSearch();
  };

  const navLinks = topCategories.map(c => {
    const key   = `nav_${c.id.replace(/-/g, '_')}`;
    const label = t(key) !== key ? t(key) : c.label;
    return { to: `/category/${c.id}`, label: `${c.icon} ${label}` };
  });

  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
      <div className="navbar__top-banner">{t('hero_top_banner')}</div>
      <div className="navbar__inner">
        <Link to="/" className="navbar__logo">
          <img src="/logo-matrix.png" alt="Matrix Electronic Devices by Maher" />
        </Link>

        <div className="navbar__links">
          {navLinks.map(l => (
            <Link
              key={l.to}
              to={l.to}
              className={location.pathname === l.to ? 'nav-link--active' : ''}
            >
              {l.label}
            </Link>
          ))}
          <Link to="/about"   className={location.pathname === '/about'   ? 'nav-link--active' : ''}>{t('nav_about')}</Link>
          <Link to="/contact" className={location.pathname === '/contact' ? 'nav-link--active' : ''}>{t('nav_contact')}</Link>
        </div>

        <div className="navbar__actions">
          {/* Search (desktop only) */}
          <div className={`navbar-search ${searchOpen ? 'navbar-search--open' : ''}`}>
            <form onSubmit={handleSearch} className="navbar-search__form">
              <input ref={inputRef} type="text" placeholder={t('nav_search_placeholder')}
                value={searchVal} onChange={e => setSearchVal(e.target.value)} className="navbar-search__input" />
              <button type="submit" className="navbar-search__submit">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
              </button>
              <button type="button" className="navbar-search__close" onClick={closeSearch}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </form>
          </div>

          {!searchOpen && (
            <button className="nav-icon-btn nav-icon-btn--search-only" onClick={openSearch}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
            </button>
          )}

          {/* Cart */}
          <button className="cart-btn" onClick={onCartOpen}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
            {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
          </button>

          {/* Mobile hamburger */}
          <button className="hamburger" onClick={() => setMenuOpen(m => !m)}>
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Mobile drawer — rendu hors de <nav> (position: fixed) pour éviter tout souci
          de containing block sur les navigateurs mobiles (notamment Safari iOS) */}
      {createPortal(
        <>
          <div className={`drawer-backdrop ${menuOpen ? 'drawer-backdrop--open' : ''}`} onClick={() => setMenuOpen(false)} />
          <div className={`drawer ${menuOpen ? 'drawer--open' : ''}`}>
            <div className="drawer__header">
              <img src="/logo-matrix.png" alt="Matrix Electronic Devices" className="drawer__logo" />
              <button className="drawer__close" onClick={() => setMenuOpen(false)} aria-label="Fermer le menu">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="drawer__nav">
              {navLinks.map(l => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`drawer__link ${location.pathname === l.to ? 'drawer__link--active' : ''}`}
                >
                  {l.label}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </Link>
              ))}
              <Link
                to="/about"
                className={`drawer__link ${location.pathname === '/about' ? 'drawer__link--active' : ''}`}
              >
                {t('nav_about')}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </Link>
              <Link
                to="/contact"
                className={`drawer__link ${location.pathname === '/contact' ? 'drawer__link--active' : ''}`}
              >
                {t('nav_contact')}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </Link>
            </div>

            <div className="drawer__footer">
              <button className="drawer__lang" onClick={toggle}>
                <span className="flag">{lang === 'en' ? '🇫🇷' : '🇬🇧'}</span>
                <span>{lang === 'en' ? 'Passer en Français' : 'Switch to English'}</span>
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </nav>
  );
};

export default Navbar;
